import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, TrendingUp, MessageCircle, Sparkles,
  Download, Search, ChevronRight, Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AdminLayout from "./AdminLayout";
import { adminApi, formatApiError } from "@/lib/adminApi";

const STATUS_META = {
  new:       { label: "Nuevo",       color: "bg-[#2c4a3b]/10 text-[#2c4a3b]" },
  contacted: { label: "Contactado",  color: "bg-[#bc6c25]/15 text-[#9c581e]" },
  converted: { label: "Convertido",  color: "bg-emerald-500/15 text-emerald-700" },
  lost:      { label: "Perdido",     color: "bg-red-500/10 text-red-600" },
};

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61]">{label}</p>
          <p className="font-serif text-4xl mt-3 text-[#1a2421] leading-none">{value}</p>
          {sub && <p className="text-xs text-[#5c6b61] mt-2">{sub}</p>}
        </div>
        <div className="w-11 h-11 rounded-2xl bg-[#bc6c25]/10 grid place-items-center text-[#bc6c25]">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function FunnelBar({ funnel }) {
  const total = Object.values(funnel).reduce((a, b) => a + b, 0) || 1;
  const segs = [
    { key: "new",       color: "#2c4a3b" },
    { key: "contacted", color: "#bc6c25" },
    { key: "converted", color: "#22c55e" },
    { key: "lost",      color: "#94a3b8" },
  ];
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] mb-4">Embudo de leads</p>
      <div className="flex h-4 rounded-full overflow-hidden bg-[#f0ebe1]">
        {segs.map((s) => (
          <div
            key={s.key}
            style={{ width: `${(funnel[s.key] / total) * 100}%`, background: s.color }}
            title={`${s.key}: ${funnel[s.key]}`}
          />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-4 gap-3 text-center">
        {segs.map((s) => (
          <div key={s.key} className="text-xs">
            <div className="w-2 h-2 rounded-full mx-auto" style={{ background: s.color }} />
            <p className="mt-1 capitalize text-[#5c6b61]">{STATUS_META[s.key].label}</p>
            <p className="font-serif text-2xl text-[#1a2421] mt-0.5">{funnel[s.key]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopProducts({ products }) {
  if (!products?.length) return null;
  const max = products[0].count;
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] mb-4">Productos más recomendados</p>
      <ul className="space-y-2.5">
        {products.slice(0, 6).map((p) => (
          <li key={p.name} className="text-sm">
            <div className="flex justify-between items-baseline">
              <span className="text-[#1a2421] truncate pr-2">{p.name}</span>
              <span className="text-xs text-[#5c6b61] font-semibold">{p.count}</span>
            </div>
            <div className="mt-1 h-1.5 bg-[#f0ebe1] rounded-full overflow-hidden">
              <div className="h-full bg-[#bc6c25]" style={{ width: `${(p.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ source: "", status: "", language: "", search: "" });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, l] = await Promise.all([
        adminApi.stats(),
        adminApi.listLeads({
          ...(filters.source && { source: filters.source }),
          ...(filters.status && { status: filters.status }),
          ...(filters.language && { language: filters.language }),
          ...(filters.search && { search: filters.search }),
          limit: 200,
        }),
      ]);
      setStats(s);
      setLeads(l);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const csvUrl = useMemo(() => {
    const token = localStorage.getItem("admin_token");
    const params = { ...filters };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    // The export endpoint accepts query params; we add the token via header — but a direct link
    // can't set headers. Use a window.fetch + blob trick on click instead.
    return { params, token };
  }, [filters]);

  const downloadCsv = async () => {
    const { params, token } = csvUrl;
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv${qs ? "?" + qs : ""}`,
      { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
    );
    if (!res.ok) { setError("No se pudo exportar"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eliora-leads-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[#1a2421] leading-tight">
            Tus <em className="text-[#bc6c25]">leads</em>
          </h1>
          <p className="text-sm text-[#5c6b61] mt-2">
            Da seguimiento, marca conversiones y descarga tu base.
          </p>
        </div>
        <Button
          onClick={downloadCsv}
          data-testid="export-csv-btn"
          variant="outline"
          className="rounded-full border-[#2c4a3b]/30 text-[#1a2421]"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {error && (
        <div className="mb-6 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* ---------- Stats ---------- */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total leads" value={stats.total} icon={Users} sub={`${stats.this_month} en 30 días`} />
          <StatCard label="Esta semana" value={stats.this_week} icon={Sparkles} sub="Nuevos en 7 días" />
          <StatCard label="Conversión" value={`${stats.conversion_rate}%`} icon={TrendingUp} sub={`${stats.funnel.converted} convertidos`} />
          <StatCard label="Por canal" value={stats.sources.quiz} icon={MessageCircle} sub={`${stats.sources.explorer} kit · ${stats.sources.quiz} quiz`} />
        </div>
      )}

      {stats && (
        <div className="grid lg:grid-cols-2 gap-4 mb-8">
          <FunnelBar funnel={stats.funnel} />
          <TopProducts products={stats.top_products} />
        </div>
      )}

      {/* ---------- Filters ---------- */}
      <form
        onSubmit={onSearchSubmit}
        data-testid="leads-filters"
        className="bg-white rounded-2xl p-4 border border-[#2c4a3b]/8 flex flex-wrap items-center gap-3 mb-4"
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#5c6b61] mr-2">
          <Filter className="w-3.5 h-3.5" /> Filtros
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5c6b61]" />
          <Input
            placeholder="Buscar por nombre, email o teléfono…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="search-input"
            className="pl-9 rounded-full bg-[#f9f6f0]/50 border-[#2c4a3b]/15"
          />
        </div>
        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          data-testid="filter-source"
          className="rounded-full bg-[#f9f6f0]/50 border border-[#2c4a3b]/15 px-4 py-2 text-sm"
        >
          <option value="">Origen</option>
          <option value="quiz">Quiz</option>
          <option value="explorer">Kit / Explorer</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="filter-status"
          className="rounded-full bg-[#f9f6f0]/50 border border-[#2c4a3b]/15 px-4 py-2 text-sm"
        >
          <option value="">Estado</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={filters.language}
          onChange={(e) => setFilters({ ...filters, language: e.target.value })}
          data-testid="filter-language"
          className="rounded-full bg-[#f9f6f0]/50 border border-[#2c4a3b]/15 px-4 py-2 text-sm"
        >
          <option value="">Idioma</option>
          <option value="es">Español</option>
          <option value="en">English</option>
        </select>
        <Button type="submit" data-testid="apply-filters-btn" className="rounded-full bg-[#2c4a3b] text-white">
          Aplicar
        </Button>
      </form>

      {/* ---------- Table ---------- */}
      <div className="bg-white rounded-2xl border border-[#2c4a3b]/8 overflow-hidden" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-[#f9f6f0]/60 text-[10px] uppercase tracking-[0.18em] text-[#5c6b61]">
            <tr>
              <th className="text-left p-4">Lead</th>
              <th className="text-left p-4">Contacto</th>
              <th className="text-left p-4">Origen</th>
              <th className="text-left p-4">Estado</th>
              <th className="text-left p-4">Fecha</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-[#5c6b61]">Cargando leads…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#5c6b61]">No hay leads para los filtros actuales.</td></tr>
            )}
            {!loading && leads.map((l) => {
              const meta = STATUS_META[l.status] || STATUS_META.new;
              return (
                <tr
                  key={l.id}
                  onClick={() => navigate(`/admin/leads/${l.id}`)}
                  className="border-t border-[#2c4a3b]/5 hover:bg-[#bc6c25]/5 transition-colors cursor-pointer"
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="p-4">
                    <p className="font-semibold text-[#1a2421]">{l.name}</p>
                    <p className="text-xs text-[#5c6b61] truncate max-w-xs">{l.message || ""}</p>
                  </td>
                  <td className="p-4 text-[#1a2421]">
                    <p>{l.email}</p>
                    <p className="text-xs text-[#5c6b61]">{l.phone || "—"}</p>
                  </td>
                  <td className="p-4">
                    <span className="text-xs uppercase tracking-wide text-[#5c6b61]">{l.source}</span>
                  </td>
                  <td className="p-4">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 ${meta.color}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-[#5c6b61] whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="p-4">
                    <Link to={`/admin/leads/${l.id}`} className="text-[#bc6c25]">
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
